import type { ReactElement } from 'react'
import type {
  CaseEndpoint,
  CreateEndpointRequest,
  UpdateEndpointRequest,
  ImportEndpointsRequest,
  CreateRelationshipRequest,
  EndpointRelationship,
  UpdateRelationshipRequest,
  BulkCreateRelationshipRequest,
  BulkCreateResponse,
  MoveRelationshipRequest,
} from '../../../types/case-container'
import type { CreateRelationshipModal } from './CreateRelationshipModal'
import {
  CollectionContextMenuOverlay,
  CreateBulkRelationshipModals,
  DeleteModalsGroup,
  EndpointModalsGroup,
  LinkContextMenuOverlay,
  MapContextMenuOverlay,
  SetModalsGroup,
  ViewEditRelationshipModals,
} from './case-infrastructure-tab-modals/case-infrastructure-tab-modals.parts'

type EndpointPosition = { x: number; y: number; endpoint: CaseEndpoint }

export interface CaseInfrastructureTabModalsProps {
  caseId: string
  endpoints: CaseEndpoint[]
  relationships: EndpointRelationship[]
  questionTemplates: Parameters<typeof CreateRelationshipModal>[0]['templates']
  selectedIds: Set<string>
  selectionCount: number
  existingCustomRoles: string[]
  creating: boolean
  deleting: boolean
  importing: boolean
  bulkDeleting: boolean
  creatingRelationship: boolean
  deletingRelationship: boolean
  movingRelationship: boolean
  copyingLinks: boolean
  copyError: string | null
  mutationError: string | null
  relationshipError: string | null
  showCreateModal: boolean
  setShowCreateModal: (v: boolean) => void
  editingEndpoint: CaseEndpoint | null
  setEditingEndpoint: (v: CaseEndpoint | null) => void
  linkingEndpoint: CaseEndpoint | null
  setLinkingEndpoint: (v: CaseEndpoint | null) => void
  showCsvImportModal: boolean
  setShowCsvImportModal: (v: boolean) => void
  showJsonImportModal: boolean
  setShowJsonImportModal: (v: boolean) => void
  showNewImportModal: boolean
  setShowNewImportModal: (v: boolean) => void
  showRelationshipImportModal: boolean
  setShowRelationshipImportModal: (v: boolean) => void
  viewLinksEndpoint: CaseEndpoint | null
  setViewLinksEndpoint: (v: CaseEndpoint | null) => void
  editingRelationship: EndpointRelationship | null
  setEditingRelationship: (v: EndpointRelationship | null) => void
  bulkLinkSourceEndpoint: CaseEndpoint | null
  setBulkLinkSourceEndpoint: (v: CaseEndpoint | null) => void
  copyLinksTargetEndpoint: CaseEndpoint | null
  setCopyLinksTargetEndpoint: (v: CaseEndpoint | null) => void
  setCopyError: (v: string | null) => void
  contextMenu: EndpointPosition | null
  setContextMenu: (v: EndpointPosition | null) => void
  mapContextMenu: EndpointPosition | null
  setMapContextMenu: (v: EndpointPosition | null) => void
  showViewMapSetModal: boolean
  setShowViewMapSetModal: (v: boolean) => void
  collectionContextMenu: EndpointPosition | null
  setCollectionContextMenu: (v: EndpointPosition | null) => void
  showViewCollectionSetModal: boolean
  setShowViewCollectionSetModal: (v: boolean) => void
  deleteContextMenu: EndpointPosition | null
  setDeleteContextMenu: (v: EndpointPosition | null) => void
  deleteConfirmation: { endpoints: CaseEndpoint[] } | null
  setDeleteConfirmation: (v: { endpoints: CaseEndpoint[] } | null) => void
  mapSetIds: Set<string>
  mapSetCount: number
  isInSet: (id: string) => boolean
  toggleMapSet: (id: string) => void
  removeManyFromMapSet: (ids: string[]) => void
  clearMapSet: () => Promise<void> | void
  collectionSetIds: Set<string>
  collectionSetCount: number
  isInCollectionSet: (id: string) => boolean
  toggleCollectionSet: (id: string) => void
  removeFromCollectionSet: (id: string) => void
  clearCollectionSet: () => void
  handleAddSelectedToMapSet: () => void
  handleAddSelectedToCollectionSet: () => void
  handleCreate: (data: CreateEndpointRequest) => Promise<void>
  handleUpdate: (data: UpdateEndpointRequest) => Promise<void>
  handleCreateRelationship: (data: CreateRelationshipRequest) => Promise<void>
  handleImport: (data: ImportEndpointsRequest) => Promise<{
    imported: number
    skipped: number
    errors: { row: number; field: string; message: string }[]
  } | null>
  handleUpdateRelationship: (data: UpdateRelationshipRequest) => void
  handleDeleteRelationship: (relationshipId: string) => Promise<void>
  handleBulkCreateRelationships: (
    data: BulkCreateRelationshipRequest
  ) => Promise<BulkCreateResponse | null>
  handleCopyLinks: (fromEndpointId: string) => Promise<BulkCreateResponse | null>
  handleMoveRelationship: (
    relationshipId: string,
    request: MoveRelationshipRequest
  ) => Promise<void>
  handleDeleteSingle: (endpoint: CaseEndpoint) => void
  handleDeleteSelected: () => void
  handleBulkDelete: () => Promise<void>
  getRelationshipCount: (endpointId: string) => number
  refetch: () => unknown
  clearError: () => void
  clearRelationshipError: () => void
}

export function CaseInfrastructureTabModals(props: CaseInfrastructureTabModalsProps): ReactElement {
  return (
    <>
      <EndpointModalsGroup {...props} />
      <CreateBulkRelationshipModals {...props} />
      <ViewEditRelationshipModals {...props} />
      <LinkContextMenuOverlay {...props} />
      <MapContextMenuOverlay {...props} />
      <CollectionContextMenuOverlay {...props} />
      <SetModalsGroup {...props} />
      <DeleteModalsGroup {...props} />
    </>
  )
}
